import React, { useMemo } from 'react';
import { Alert, Button } from '@saleshandy/design-system';
import { Bulb, Check, Cross } from '@saleshandy/icons';

import Modal from '../../../../../../../../shared/design-system/components/atoms/modal';

import { Activate, Recurring } from '../../../../../../../../shared/svg';
import { getIsPlanUpgradeScheduled } from '../../../../../../../../shared/utils/user-details';
import SubscriptionStatus from '../../../../enums/subscription-status';
import { OverlayTooltip } from '../../../../../../../../shared/design-system/components/overlay';

const options = [
  {
    id: 'payNow',
    icon: <Activate />,
    title: 'Activate Now',
    desc:
      'Your billing resets today. Unused email sending limits will carry forward.',
    features: [
      { id: 1, label: 'Instant access to Unlimited prospect upload' },
      { id: 2, label: 'Payment processed immediately' },
    ],
    btnLabel: 'Pay Now',
    isRecommended: true,
  },
  {
    id: 'scheduleLater',
    icon: <Recurring />,
    title: 'Activate on Next Billing Cycle',
    desc:
      'No Payment required now. Your plan will be updated on your next billing cycle.',
    features: [
      { id: 1, label: 'Feature activate at renewal' },
      { id: 2, label: 'Current billing cycle remains unchanged' },
    ],
    btnLabel: 'Schedule for Next Billing Cycle',
  },
];

type IProps = {
  show: boolean;
  onCancel: VoidFunction;
  onPayNow: VoidFunction;
  onSchedule: VoidFunction;
  isScheduleRequestLoading: boolean;
  subscriptionStatus: SubscriptionStatus;
};

const UpdatePlanModal: React.FC<IProps> = ({
  show,
  onCancel,
  onPayNow,
  onSchedule,
  isScheduleRequestLoading,
  subscriptionStatus,
}) => {
  const isPlanUpgradeScheduled = getIsPlanUpgradeScheduled();

  const scheduleLaterTooltipText = useMemo(() => {
    if (isPlanUpgradeScheduled) {
      return ' You’ve already scheduled a plan update for the next billing cycle.';
    }

    if (subscriptionStatus === SubscriptionStatus.Paused) {
      return 'Your subscription is currently paused, so plan updates can’t be scheduled.';
    }

    if (subscriptionStatus === SubscriptionStatus.Canceled) {
      return 'Your subscription is canceled, so you can’t schedule a plan update.';
    }

    return '';
  }, [isPlanUpgradeScheduled, subscriptionStatus]);

  return (
    <Modal
      show={show}
      className="update-plan-modal"
      onHide={onCancel}
      hideHeader
      hideFooter
    >
      <div className="update-plan-modal__container">
        <div className="update-plan-modal__header">
          <h1>Update Your Current Plan</h1>

          <button type="button" onClick={onCancel}>
            <Cross />
          </button>
        </div>
        <div className="update-plan-modal__content">
          {options.map((option) => {
            const btn = (
              <Button
                variant={option?.isRecommended ? 'primary' : 'secondary'}
                isFullWidth
                onClick={option.id === 'payNow' ? onPayNow : onSchedule}
                disabled={
                  option.id === 'payNow'
                    ? false
                    : isScheduleRequestLoading ||
                      isPlanUpgradeScheduled ||
                      subscriptionStatus !== SubscriptionStatus.Active
                }
                isLoading={
                  option.id === 'payNow' ? false : isScheduleRequestLoading
                }
                loadingText={option.btnLabel}
              >
                {option.btnLabel}
              </Button>
            );

            return (
              <div
                key={option.id}
                className={`update-plan-option ${
                  option?.isRecommended ? 'recommended' : ''
                }`}
              >
                <div className="update-plan-option-icon">{option.icon}</div>
                <div className="update-plan-option-content">
                  <h1>{option.title}</h1>
                  <p>{option.desc}</p>
                </div>
                <div className="update-plan-option-features">
                  {option.features.map((feature) => (
                    <div key={feature.id} className="feature">
                      <Check />
                      <p>{feature.label}</p>
                    </div>
                  ))}
                </div>

                {option.id === 'payNow' || !scheduleLaterTooltipText ? (
                  btn
                ) : (
                  <OverlayTooltip
                    text={
                      <div className="content">{scheduleLaterTooltipText}</div>
                    }
                    className="update-plan-tooltip"
                  >
                    {btn}
                  </OverlayTooltip>
                )}
              </div>
            );
          })}
        </div>
        <div className="update-plan-modal__footer">
          <Alert
            variant="info"
            icon={Bulb}
            description='Choose "Pay Now" for immediate access to unlimited prospect uploads, or "Upgrade Later" if you prefer to wait until your next billing cycle.'
          />
        </div>
      </div>
    </Modal>
  );
};

export default UpdatePlanModal;
